// ============================================================
// cf-json-builder.js � CF.jsonBuilder
// Reusable JSON key-value builder component.
// Load only on pages that require structured JSON input.
//
// Dependencies: none (standalone)
//
// Provides CF.jsonBuilder with:
//   CF.jsonBuilder.init(containerSelector, options) ? handle
//
// Handle API:
//   handle.setJson(jsonStr)        � populate builder from a JSON string
//   handle.getJson()               � return current JSON as a string
//   handle.addRow(key, val)        � programmatically append a row
//   handle.clear()                 � remove all rows and clear output
//
// Options:
//   hiddenInputId  {string}    ID of the hidden input/textarea that is kept
//                              in sync with the current JSON value.
//   addBtnId       {string}    ID of the "Add Field" button.
//   previewId      {string}    ID of the <pre> element for live JSON preview.
//   previewWrapId  {string}    ID of the preview wrapper (hidden when empty).
//   colorKeys      {string[]}  Key names that render a color-picker instead of
//                              a plain text input.
//                              Default: ['color','bgcolor','background',
//                                        'bordercolor','textcolor']
//   onChange       {function}  Callback(jsonStr) fired on every change.
//
// Typical HTML markup in cshtml:
// ?????????????????????????????????????????????????????????????
//   <div class="cf-form-group">
//       <label class="form-label">Extra Data</label>
//       <div id="p-ed-builder" class="cf-jb-container"></div>
//       <button type="button" id="p-ed-add-btn" class="cf-jb-add-btn">
//           <i class="fa-solid fa-plus"></i> Add Field
//       </button>
//       <div id="p-ed-preview-wrap" class="cf-jb-preview-wrap" style="display:none">
//           <div class="cf-jb-preview-label">JSON Preview</div>
//           <pre id="p-ed-preview" class="cf-jb-preview"></pre>
//       </div>
//       <textarea id="p-extradata" style="display:none"></textarea>
//       <p class="cf-code-hint">Used for custom rendering (color, icon, etc.)</p>
//   </div>
//
// Typical initialization in page script:
// ?????????????????????????????????????????????????????????????
//   var _jsonBuilder = CF.jsonBuilder.init('#p-ed-builder', {
//       hiddenInputId: 'p-extradata',
//       addBtnId:      'p-ed-add-btn',
//       previewId:     'p-ed-preview',
//       previewWrapId: 'p-ed-preview-wrap'
//   });
//
//   // On modal open (create):
//   _jsonBuilder.clear();
//
//   // On modal open (edit):
//   _jsonBuilder.setJson(d.extraData || '');
// ============================================================

window.CF = window.CF || {};

CF.jsonBuilder = (function () {
    'use strict';

    var DEFAULT_COLOR_KEYS = ['color', 'bgcolor', 'background', 'bordercolor', 'textcolor'];

    // ????????????????????????????????????????????????????????
    // init � attach a JSON builder to a container element
    // ????????????????????????????????????????????????????????
    function init(containerSel, options) {
        var opts = options || {};

        var container = typeof containerSel === 'string'
            ? document.querySelector(containerSel)
            : containerSel;

        if (!container) {
            if (window.console) console.warn('CF.jsonBuilder: container not found:', containerSel);
            return null;
        }

        var colorKeys   = opts.colorKeys    || DEFAULT_COLOR_KEYS;
        var hiddenInput = opts.hiddenInputId ? document.getElementById(opts.hiddenInputId) : null;
        var addBtn      = opts.addBtnId      ? document.getElementById(opts.addBtnId)      : null;
        var previewEl   = opts.previewId     ? document.getElementById(opts.previewId)     : null;
        var previewWrap = opts.previewWrapId ? document.getElementById(opts.previewWrapId) : null;

        // ?? Wire up the Add-Field button ?????????????????????
        if (addBtn) {
            // Guard against double-binding if init() is called again on the same btn
            if (!addBtn._cfJbBound) {
                addBtn._cfJbBound = true;
                addBtn.addEventListener('click', function () {
                    _appendRow('', '');
                    _sync();
                    var rows = container.querySelectorAll('.cf-jb-row');
                    if (rows.length) {
                        var keyInput = rows[rows.length - 1].querySelector('.cf-jb-key');
                        if (keyInput) setTimeout(function () { keyInput.focus(); }, 50);
                    }
                });
            }
        }

        // ?? Helpers ??????????????????????????????????????????
        function _isColorKey(k) {
            return colorKeys.indexOf((k || '').toLowerCase().trim()) >= 0;
        }

        function _getRowVal(valWrap) {
            var v = valWrap.querySelector('.cf-jb-val');
            return v ? v.value : '';
        }

        function _isColorWrap(valWrap) {
            return !!valWrap.querySelector('.cf-jb-color-wrap');
        }

        // ?? Build the value cell (plain text OR color picker) ?
        function _buildValInput(valWrap, isColor, val) {
            if (isColor) {
                var colorWrap = document.createElement('div');
                colorWrap.className = 'cf-jb-color-wrap';

                var picker = document.createElement('input');
                picker.type = 'color';
                picker.className = 'cf-jb-color-input';
                picker.title = 'Pick a color';
                var safeHex = /^#[0-9A-Fa-f]{3,6}$/.test(val) ? val : '#6366F1';
                picker.value = safeHex;

                var hexInput = document.createElement('input');
                hexInput.type = 'text';
                hexInput.className = 'form-input cf-jb-val cf-jb-hex';
                hexInput.placeholder = '#6366F1';
                hexInput.value = val || '';
                hexInput.maxLength = 7;
                hexInput.spellcheck = false;

                picker.addEventListener('input', function () {
                    hexInput.value = picker.value;
                    _sync();
                });
                hexInput.addEventListener('input', function () {
                    if (/^#[0-9A-Fa-f]{3,6}$/.test(hexInput.value)) {
                        picker.value = hexInput.value;
                    }
                    _sync();
                });

                colorWrap.appendChild(picker);
                colorWrap.appendChild(hexInput);
                valWrap.appendChild(colorWrap);
            } else {
                var textInput = document.createElement('input');
                textInput.type = 'text';
                textInput.className = 'form-input cf-jb-val';
                textInput.placeholder = 'value';
                textInput.value = val || '';
                textInput.addEventListener('input', _sync);
                valWrap.appendChild(textInput);
            }
        }

        // ?? Append a single row to the container ?????????????
        function _appendRow(key, val) {
            var isColor = _isColorKey(key);

            var row = document.createElement('div');
            row.className = 'cf-jb-row';

            // Key input
            var keyInput = document.createElement('input');
            keyInput.type = 'text';
            keyInput.className = 'form-input cf-jb-key';
            keyInput.placeholder = 'key';
            keyInput.value = key || '';
            keyInput.spellcheck = false;

            // Value wrapper (rebuilt when key changes type)
            var valWrap = document.createElement('div');
            valWrap.className = 'cf-jb-val-wrap';
            _buildValInput(valWrap, isColor, val || '');

            // Remove button
            var removeBtn = document.createElement('button');
            removeBtn.type = 'button';
            removeBtn.className = 'cf-jb-remove-btn';
            removeBtn.title = 'Remove field';
            removeBtn.innerHTML = '<i class="fa-solid fa-xmark"></i>';
            removeBtn.addEventListener('click', function () {
                row.remove();
                _sync();
            });

            // Re-render value input when key changes between color / plain text
            keyInput.addEventListener('input', function () {
                var newIsColor = _isColorKey(keyInput.value.trim());
                if (newIsColor !== _isColorWrap(valWrap)) {
                    var curVal = _getRowVal(valWrap);
                    valWrap.innerHTML = '';
                    _buildValInput(valWrap, newIsColor, curVal);
                }
                _sync();
            });

            row.appendChild(keyInput);
            row.appendChild(valWrap);
            row.appendChild(removeBtn);
            container.appendChild(row);
        }

        // ?? Collect all rows ? sync hidden input + preview ???
        function _sync() {
            var rows = container.querySelectorAll('.cf-jb-row');
            var obj  = {};

            rows.forEach(function (row) {
                var k = row.querySelector('.cf-jb-key');
                var v = row.querySelector('.cf-jb-val');
                if (k && v && k.value.trim()) {
                    obj[k.value.trim()] = v.value;
                }
            });

            var hasKeys = Object.keys(obj).length > 0;
            var jsonStr = hasKeys ? JSON.stringify(obj) : '';

            if (hiddenInput) hiddenInput.value = jsonStr;

            if (previewEl) {
                previewEl.textContent = hasKeys ? JSON.stringify(obj, null, 2) : '';
            }
            if (previewWrap) {
                previewWrap.style.display = hasKeys ? '' : 'none';
            }

            if (typeof opts.onChange === 'function') {
                opts.onChange(jsonStr);
            }
        }

        // ?? Public handle ????????????????????????????????????
        return {
            /**
             * Populate the builder from a JSON string.
             * Any existing rows are cleared first.
             * @param {string} jsonStr
             */
            setJson: function (jsonStr) {
                container.innerHTML = '';
                if (!jsonStr || jsonStr === '{}' || jsonStr.trim() === '') {
                    _sync();
                    return;
                }
                var obj;
                try { obj = JSON.parse(jsonStr); } catch (e) { _sync(); return; }
                Object.keys(obj).forEach(function (k) {
                    _appendRow(k, obj[k] != null ? String(obj[k]) : '');
                });
                _sync();
            },

            /**
             * Return the current JSON string (same value as the hidden input).
             * @returns {string}
             */
            getJson: function () {
                return hiddenInput ? hiddenInput.value : '';
            },

            /**
             * Programmatically append a key-value row.
             * @param {string} key
             * @param {string} val
             */
            addRow: function (key, val) {
                _appendRow(key || '', val || '');
                _sync();
            },

            /**
             * Remove all rows and reset the hidden input to empty.
             */
            clear: function () {
                container.innerHTML = '';
                _sync();
            }
        };
    }

    return { init: init };
})();
